import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { axiosInstance } from "../../api/api"
import { TabScreens } from '../../routes'
import { actions } from '../../store/actions'
import { usePermission } from '../../hooks/usePermission';
import { SearchSvg } from '../../assets/svg/SearchSvg';

interface PatrolAsset {
  id: number
  name: string
  longitude: number
  latitude: number
  radius: number
  code: string
  write_at?: string
  written_to_card?: boolean
}

interface NDEFRecordInit {
  recordType: string;
  mediaType?: string;
  id?: string;
  data?: string | ArrayBuffer;
}

interface NDEFMessageInit {
  records: NDEFRecordInit[];
}

interface NDEFReader {
  write(message: NDEFMessageInit | string): Promise<void>;
  scan(): Promise<void>;
  onreading?: (event: any) => void;
  onreadingerror?: (event: any) => void;
}

declare var NDEFReader: {
  prototype: NDEFReader;
  new(): NDEFReader;
};

const AlertModal: React.FC<{
  type: 'success' | 'error' | 'warning' | 'info' | 'confirm';
  title: string;
  message: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  onClose: () => void;
  confirmText?: string;
  cancelText?: string;
}> = ({ type, title, message, onConfirm, onCancel, onClose, confirmText = 'Ya', cancelText = 'Batal' }) => {
  const getColors = () => {
    switch (type) {
      case 'success':
        return {
          bg: '#F0FDF4',
          border: '#BBF7D0',
          text: '#166534',
          button: '#10B981',
          buttonHover: '#059669'
        };
      case 'error':
        return {
          bg: '#FEF2F2',
          border: '#FECACA',
          text: '#DC2626',
          button: '#EF4444',
          buttonHover: '#DC2626'
        };
      case 'warning':
        return {
          bg: '#FFFBEB',
          border: '#FED7AA',
          text: '#D97706',
          button: '#F59E0B',
          buttonHover: '#D97706'
        };
      case 'info':
        return {
          bg: '#EFF6FF',
          border: '#BFDBFE',
          text: '#1E40AF',
          button: '#3B82F6',
          buttonHover: '#2563EB'
        };
      case 'confirm':
        return {
          bg: '#F9FAFB',
          border: '#E5E7EB',
          text: '#374151',
          button: '#3B82F6',
          buttonHover: '#2563EB'
        };
      default:
        return {
          bg: '#F9FAFB',
          border: '#E5E7EB',
          text: '#374151',
          button: '#3B82F6',
          buttonHover: '#2563EB'
        };
    }
  };

  const colors = getColors();

  const getIcon = () => {
    switch (type) {
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      case 'warning':
        return '⚠️';
      case 'info':
        return 'ℹ️';
      case 'confirm':
        return '❓';
      default:
        return 'ℹ️';
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 10000,
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '12px',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        width: '100%',
        maxWidth: '400px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Modal Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          backgroundColor: colors.bg,
          borderLeft: `4px solid ${colors.border}`
        }}>
          <span style={{ fontSize: '1.25rem' }}>{getIcon()}</span>
          <h3 style={{
            fontSize: '1.125rem',
            fontWeight: 600,
            color: colors.text,
            margin: 0
          }}>
            {title}
          </h3>
        </div>

        {/* Modal Content */}
        <div style={{
          padding: '1.5rem'
        }}>
          <p style={{
            color: '#6B7280',
            fontSize: '14px',
            lineHeight: '1.5',
            margin: 0
          }}>
            {message}
          </p>
        </div>

        {/* Modal Footer */}
        <div style={{
          padding: '1rem 1.5rem',
          borderTop: '1px solid #E5E7EB',
          display: 'flex',
          justifyContent: type === 'confirm' ? 'space-between' : 'flex-end',
          gap: '0.75rem',
          backgroundColor: '#F9FAFB'
        }}>
          {type === 'confirm' && (
            <button
              onClick={onCancel || onClose}
              style={{
                padding: '10px 20px',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                backgroundColor: '#FFFFFF',
                color: '#374151',
                fontWeight: 500,
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#FFFFFF'}
            >
              {cancelText}
            </button>
          )}
          
          <button
            onClick={onConfirm || onClose}
            style={{
              padding: '10px 20px',
              backgroundColor: colors.button,
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 500,
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = colors.buttonHover}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = colors.button}
          >
            {type === 'confirm' ? confirmText : 'Tutup'}
          </button>
        </div>
      </div>
    </div>
  );
};

export const KelolaPatrol: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [assets, setAssets] = useState<PatrolAsset[]>([])
  const [loading, setLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingAsset, setEditingAsset] = useState<PatrolAsset | null>(null)
  const [wordCount, setWordCount] = useState(0)
  const { canIndex, canStore, canUpdate, canDelete } = usePermission('patrol_asset_index');
  const [searchQuery, setSearchQuery] = useState("")
  const [searchLoading, setSearchLoading] = useState(false)

  const [formData, setFormData] = useState({
    name: '',
    code: '',
  })

  const [isMobile, setIsMobile] = useState(false);

  const [nfcModalOpen, setNfcModalOpen] = useState(false)
  const [nfcAsset, setNfcAsset] = useState<PatrolAsset | null>(null)
  const [nfcStatus, setNfcStatus] = useState<string | null>(null)
  const [nfcError, setNfcError] = useState<string | null>(null)
  const [supported] = useState<boolean>(() => typeof window !== 'undefined' && 'NDEFReader' in window)
  const ndefRef = useRef<NDEFReader | null>(null)
  const [alert, setAlert] = useState<{
    show: boolean;
    type: 'success' | 'error' | 'warning' | 'info' | 'confirm';
    title: string;
    message: string;
    onConfirm?: () => void;
  }>({
    show: false,
    type: 'info',
    title: '',
    message: ''
  });

  const showAlert = (type: 'success' | 'error' | 'warning' | 'info' | 'confirm', title: string, message: string, onConfirm?: () => void) => {
    setAlert({
      show: true,
      type,
      title,
      message,
      onConfirm
    });
  };

  const closeAlert = () => {
    setAlert(prev => ({ ...prev, show: false }));
  };

  const countWords = (text: string) => {
    return text.trim() ? text.trim().split(/\s+/).length : 0;
  };

  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const fetchAllAssets = async (searchFilter = "") => {
    setLoading(true);
    try {
      let allAssets: any[] = [];
      let currentPage = 1;
      let hasMorePages = true;

      while (hasMorePages) {
        const params: any = { 
          page: currentPage,
          sort_by: "created_at",
          sort_type: "desc",
          per_page: 10
        };

        if (searchFilter) {
          params.filter = searchFilter;
        }

        const res = await axiosInstance.get('/api/v1/patrol/asset/index', {
          params
        });
        
        const pageData = (res.data.data || []).map((item: any) => ({
          ...item,
          id: item.id,
          name: item.name,
          code: item.code,
          longitude: parseFloat(item.longitude),
          latitude: parseFloat(item.latitude),
          radius: parseFloat(item.radius),
          write_at: item.write_at,
          written_to_card: !!item.write_at
        }));
        
        allAssets = [...allAssets, ...pageData];
        
        hasMorePages = currentPage < res.data.last_page;
        currentPage++;
        
        if (currentPage > 20) {
          break;
        }
      }
      
      setAssets(allAssets);
      
      if (allAssets.length === 0) {

      }
    } catch (err) {
      showAlert('error', 'Gagal', 'Gagal memuat data spot patroli');
    }
    setLoading(false);
    setSearchLoading(false);
  }

  useEffect(() => { 
    fetchAllAssets(); 
  }, [])

  const handleSearch = (query: string) => {
    setSearchQuery(query);

      if (query.length < 3 && query.length > 0) {
    setSearchLoading(false);
    return;
  }


    setSearchLoading(true);
  
    const timeoutId = setTimeout(() => {
      fetchAllAssets(query);
    }, 500);

    return () => clearTimeout(timeoutId);
  };

  const openModal = (asset?: PatrolAsset) => {
    if (asset) {
      setEditingAsset(asset)
      setFormData({
        name: asset.name,
        code: asset.code,
      })
      setWordCount(countWords(asset.name))
    } else {
      setEditingAsset(null)
      setFormData({ name: '', code: '' })
      setWordCount(0)
    }
    setModalOpen(true)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    if (name === 'name') {
      const words = countWords(value);
      if (words <= 20) {
        setFormData({ ...formData, [name]: value });
        setWordCount(words);
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  }

  const handleSubmit = async () => {
    try {
      if (!formData.name.trim()) { 
        showAlert('error', 'Peringatan', 'Nama harus diisi'); 
        return 
      }

      if (wordCount > 20) {
        showAlert('error', 'Peringatan', 'Nama spot maksimal 20 kata');
        return;
      }

      const payload = {
        name: formData.name,
        code: formData.code,
        latitude: 0,
        longitude: 0, 
        radius: 0,
      };

      if (editingAsset) {
        await axiosInstance.post(`/api/v1/patrol/asset/update/${editingAsset.id}`, payload)
        showAlert('success', 'Berhasil', 'Spot berhasil diupdate')
      } else {
        await axiosInstance.post('/api/v1/patrol/asset/store', payload)
        showAlert('success', 'Berhasil', 'Spot berhasil ditambahkan')
      }
      setModalOpen(false)
      fetchAllAssets(searchQuery)
    } catch (err) { 
      showAlert('error', 'Gagal', 'Gagal menyimpan spot') 
    }
  }

  const confirmDelete = (asset: PatrolAsset) => { 
    showAlert(
      'confirm',
      'Konfirmasi Hapus',
      `Apakah Anda yakin ingin menghapus spot "${asset.name}"? Tindakan ini tidak dapat dibatalkan.`,
      () => handleDelete(asset.id)
    );
  }

  const handleDelete = async (id: number) => {
    try { 
      await axiosInstance.delete(`/api/v1/patrol/asset/destroy/${id}`);
      setAssets(prev => prev.filter(asset => asset.id !== id));
      showAlert('success', 'Berhasil', 'Spot berhasil dihapus');
    } catch (error: any) { 
      fetchAllAssets(searchQuery);
      const errorMessage = error.response?.data?.message || 'Gagal menghapus spot';
      showAlert('error', 'Gagal', errorMessage);
    }
  }

  const handleBack = () => {
    dispatch(actions.setScreen(TabScreens.Admin))
    navigate("/tab-navigator", { replace: true })
  }

  const openNfcModal = (asset: PatrolAsset) => {
    setNfcAsset(asset)
    setNfcStatus(null)
    setNfcError(null)
    setNfcModalOpen(true)
  }

  const writeTag = async () => {
    if (!nfcAsset) return
    setNfcError(null)
    if (!supported) { 
      setNfcError('Web NFC tidak didukung di browser ini')
      return 
    }

    try {
      const spotData = {
        code: nfcAsset.code,   
        name: nfcAsset.name,    
        id: nfcAsset.id        
      }

      const records = [
        {
          recordType: "url",
          data: `patrolapp://home?code=${nfcAsset.code}&name=${encodeURIComponent(nfcAsset.name)}&id=${nfcAsset.id}`
        },
        {
          recordType: "text", 
          data: JSON.stringify(spotData)
        }
      ];

      const ndef = new NDEFReader()
      ndefRef.current = ndef
      setNfcStatus('Sentuh tag NFC untuk menulis...')
      await ndef.write({ records })
      setNfcStatus(`Tag NFC berhasil ditulis ✅\nData: ${nfcAsset.name} (${nfcAsset.code})`)

      await axiosInstance.get(`/api/v1/patrol/asset/mark/${nfcAsset.id}`)
      await fetchAllAssets(searchQuery)

    } catch (e: unknown) {
      if (e instanceof Error) setNfcError(e.message)
      else setNfcError(String(e))
      setNfcStatus(null)
    }
  }

  const cssVariables = {
    primary: '#007AFF',
    primaryLight: '#EBF5FF',
    primaryDark: '#0056b3',
    backgroundLight: '#F9FAFB',
    backgroundDark: '#111827',
    cardLight: '#FFFFFF',
    cardDark: '#1F2937',
    textLight: '#1F2937',
    textDark: '#F9FAFB',
    subtextLight: '#6B7280',
    subtextDark: '#9CA3AF',
    borderLight: '#E5E7EB',
    borderDark: '#374151',
    danger: '#EF4444',
    dangerLight: '#FEE2E2',
    successLight: '#D1FAE5',
    successDark: '#065F46'
  }

  const mobileSafeArea = isMobile ? '80px' : '0px';
  const headerHeight = '80px';
  const addButtonHeight = '60px';
  const searchBarHeight = '60px';
  const scrollAreaHeight = `calc(100vh - ${headerHeight} - ${addButtonHeight} - ${searchBarHeight} - ${mobileSafeArea})`;

  const styles: { [key: string]: React.CSSProperties } = {
    container: { 
      minHeight: '100vh',
      backgroundColor: cssVariables.backgroundLight,
      fontFamily: "'Inter', sans-serif",
      padding: 0,
      margin: 0,
      paddingBottom: mobileSafeArea
    },
    header: {
      backgroundColor: cssVariables.cardLight,
      padding: '16px',
      position: 'sticky' as const,
      top: 0,
      zIndex: 10,
      borderBottom: `1px solid ${cssVariables.borderLight}`,
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      height: headerHeight,
      boxSizing: 'border-box' as const
    },
    headerContent: {
      maxWidth: '1200px',
      margin: '0 auto',
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      height: '100%'
    },
    backButton: {
      background: 'none',
      border: 'none',
      padding: '8px',
      borderRadius: '50%',
      cursor: 'pointer',
      color: cssVariables.textLight,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 0.2s',
      flexShrink: 0
    },
    headerInfo: {
      flex: 1,
      textAlign: 'left' as const
    },
    title: {
      fontSize: '20px',
      fontWeight: '700',
      color: cssVariables.textLight,
      margin: '0 0 4px 0'
    },
    subtitle: {
      fontSize: '14px',
      color: cssVariables.subtextLight,
      margin: 0
    },
    main: {
      padding: '16px',
      maxWidth: '1200px',
      margin: '0 auto',
      height: `calc(100vh - ${headerHeight} - ${mobileSafeArea})`,
      display: 'flex',
      flexDirection: 'column'
    },
    addButtonContainer: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      marginBottom: '16px',
      flexShrink: 0,
      height: addButtonHeight
    },
    addButton: {
      backgroundColor: cssVariables.primary,
      color: 'white',
      fontWeight: '600',
      padding: '12px 24px',
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      transition: 'background-color 0.3s',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      display: 'flex',
      alignItems: 'center', 
      justifyContent: 'center',
      height: '44px',
      width: '100%',
      maxWidth: '100%'
    },
    searchContainer: {
      marginBottom: '16px',
      flexShrink: 0,
      width: '100%',
      height: searchBarHeight,
      display: 'flex',
      alignItems: 'center'
    },
    searchInputWrapper: {
      position: 'relative',
      width: '100%',
      display: 'flex',
      alignItems: 'center'
    },
    searchInput: {
      width: '100%',
      padding: '12px 16px 12px 48px',
      border: `1px solid ${cssVariables.borderLight}`,
      borderRadius: '8px',
      fontSize: '14px',
      backgroundColor: 'white',
      transition: 'border-color 0.2s, box-shadow 0.2s',
      height: '48px',
      boxSizing: 'border-box' as const
    },
    searchIconWrapper: {
      position: 'absolute',
      left: '16px',
      top: '50%',
      transform: 'translateY(-50%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2
    },
    searchSvg: {
      width: '20px',
      height: '20px',
      color: cssVariables.subtextLight
    },
    searchLoadingSpinner: {
      width: '20px',
      height: '20px',
      border: '2px solid #f3f3f3',
      borderTop: `2px solid ${cssVariables.primary}`,
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    },
    searchResultsInfo: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '16px',
      padding: '12px 16px',
      backgroundColor: cssVariables.backgroundLight,
      borderRadius: '8px',
      border: `1px solid ${cssVariables.borderLight}`
    },
    clearSearchButton: {
      background: cssVariables.subtextLight,
      color: 'white',
      border: 'none',
      padding: '6px 12px',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '12px',
      fontWeight: '500',
      transition: 'background-color 0.2s'
    },
    scrollContainer: {
      flex: 1,
      overflowY: 'auto',
      minHeight: 0
    },
    grid: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      paddingBottom: '8px'
    },
    card: {
      backgroundColor: cssVariables.cardLight,
      borderRadius: '12px',
      border: `1px solid ${cssVariables.borderLight}`,
      padding: '16px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: '12px'
    },
    cardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '12px'
    },
    cardTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: cssVariables.textLight,
      margin: 0,
      flex: 1,
      wordBreak: 'break-word' as const,
      lineHeight: '1.4',
    },
    statusBadge: {
      fontSize: '12px',
      fontWeight: '600',
      padding: '4px 12px',
      borderRadius: '9999px',
      display: 'inline-block',
      flexShrink: 0,
      whiteSpace: 'nowrap'
    },
    cardActions: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      gap: '12px',
      flexWrap: 'wrap'
    },
    textButton: {
      background: 'none',
      border: 'none',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      padding: '6px 12px',
      transition: 'color 0.2s',
      color: cssVariables.primary,
      borderRadius: '6px',
      flexShrink: 0
    },
    deleteButton: {
      background: 'none',
      border: 'none',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      padding: '6px 12px',
      transition: 'color 0.2s',
      color: cssVariables.danger,
      borderRadius: '6px',
      flexShrink: 0
    },
    nfcButton: {
      backgroundColor: cssVariables.primaryLight,
      color: cssVariables.primary,
      fontSize: '14px',
      fontWeight: '500',
      padding: '6px 16px',
      borderRadius: '6px',
      border: 'none',
      cursor: 'pointer',
      transition: 'background-color 0.3s',
      flexShrink: 0
    },
    loadingContainer: {
      textAlign: 'center',
      padding: '60px',
      color: cssVariables.subtextLight,
      fontSize: '16px',
      backgroundColor: cssVariables.cardLight,
      borderRadius: '12px',
      border: `1px solid ${cssVariables.borderLight}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: '16px',
      flex: 1
    },
    emptyContainer: {
      textAlign: 'center',
      padding: '60px',
      color: cssVariables.subtextLight,
      fontSize: '16px',
      backgroundColor: cssVariables.cardLight,
      borderRadius: '12px',
      border: `1px solid ${cssVariables.borderLight}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: '16px',
      flex: 1
    },
    spinner: {
      width: '24px',
      height: '24px',
      border: '3px solid #f3f3f6',
      borderTop: `3px solid ${cssVariables.primary}`,
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      margin: '0 auto'
    }
  }

  const baseModalStyles: { [key: string]: React.CSSProperties } = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '16px',
      zIndex: 1000,
      paddingBottom: `calc(16px + ${mobileSafeArea})`
    },
    modal: {
      backgroundColor: 'white',
      padding: '24px',
      borderRadius: '12px',
      width: '100%',
      maxWidth: '500px',
      boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
      maxHeight: `calc(90vh - ${mobileSafeArea})`,
      overflow: 'auto'
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '20px'
    },
    modalTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: cssVariables.textLight,
      margin: 0
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: '20px',
      padding: '4px',
      borderRadius: '4px',
      color: cssVariables.subtextLight,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '32px',
      height: '32px'
    },
    formGroup: {
      marginBottom: '20px'
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '500',
      color: cssVariables.textLight,
      marginBottom: '8px'
    },
    input: {
      width: '100%',
      padding: '12px 16px',
      border: `1px solid ${cssVariables.borderLight}`,
      borderRadius: '8px',
      fontSize: '14px',
      boxSizing: 'border-box' as const,
      transition: 'border-color 0.2s ease',
      backgroundColor: 'white'
    },
    buttonGroup: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end',
      marginTop: '24px'
    },
    cancelButton: {
      padding: '12px 20px',
      backgroundColor: cssVariables.subtextLight,
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease'
    },
    submitButton: {
      padding: '12px 20px',
      backgroundColor: cssVariables.primary,
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease'
    }
  }

  const modalStyles: { [key: string]: React.CSSProperties } = {
    ...baseModalStyles
  }

  const nfcModalStyles: { [key: string]: React.CSSProperties } = {
    ...baseModalStyles,
    modal: {
      ...baseModalStyles.modal,
      maxWidth: '400px',
      textAlign: 'center' as const
    },
    nfcStatus: {
      padding: '16px',
      borderRadius: '8px',
      marginBottom: '20px',
      fontSize: '14px',
      fontWeight: '500',
      textAlign: 'center' as const
    },
    nfcSuccess: {
      backgroundColor: cssVariables.successLight,
      color: cssVariables.successDark,
      border: `1px solid ${cssVariables.successLight}`
    },
    nfcError: {
      backgroundColor: cssVariables.dangerLight,
      color: cssVariables.danger,
      border: `1px solid ${cssVariables.dangerLight}`
    },
    nfcInfo: {
      backgroundColor: cssVariables.primaryLight,
      color: cssVariables.primary,
      border: `1px solid ${cssVariables.primaryLight}`
    }
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <button 
            onClick={handleBack} 
            style={styles.backButton}
          >
            <span style={{ fontSize: '20px' }}>←</span>
          </button>
          <div style={styles.headerInfo}>
            <h1 style={styles.title}>Kelola Spot Patroli</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main style={styles.main}>
        <div style={styles.addButtonContainer}>
          {canStore && (
            <button 
              style={styles.addButton}
              onClick={() => openModal()}
            >
              + Tambah Spot
            </button>
          )}
        </div>

        {/* Search Bar*/}
        <div style={styles.searchContainer}>
          <div style={styles.searchInputWrapper}>
            <div style={styles.searchIconWrapper}>
              {searchLoading ? (
                <div style={styles.searchLoadingSpinner} />
              ) : (
                <SearchSvg style={styles.searchSvg} />
              )}
            </div>
            <input
              type="text"
              placeholder="Cari spot patroli berdasarkan nama..."
              style={styles.searchInput}
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              onFocus={(e) => e.target.style.borderColor = cssVariables.primary}
              onBlur={(e) => e.target.style.borderColor = cssVariables.borderLight}
            />
          </div>
        </div>

        {loading && canIndex && (
          <div style={styles.loadingContainer}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
              <div style={styles.spinner}></div>
              <div>Memuat data spot patroli...</div>
            </div>
          </div>
        )}

        {!loading && assets.length === 0 && canIndex && (
          <div style={styles.emptyContainer}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
              <div style={{ fontSize: '18px', fontWeight: '600', color: cssVariables.textLight }}>
                {searchQuery ? "Spot Patroli Tidak Ditemukan" : "Belum Ada Spot Patroli"}
              </div>
              {!searchQuery && (
                <button 
                  style={styles.addButton}
                  onClick={() => openModal()}
                >
                  Tambah Spot Pertama
                </button>
              )}
              {searchQuery && (
                <button 
                  style={styles.addButton}
                  onClick={() => {
                    setSearchQuery("");
                    fetchAllAssets();
                  }}
                >
                  Tampilkan Semua Spot
                </button>
              )}
            </div>
          </div>
        )}

        {!loading && assets.length > 0 && canIndex && (
          <div style={styles.scrollContainer}>
            {searchQuery && (
                <div style={{
                  display: 'flex',
                  flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
                  justifyContent: 'space-between',
                  alignItems: window.innerWidth <= 768 ? 'flex-start' : 'center',
                  gap: '12px',
                  marginBottom: '16px',
                  padding: '12px 16px',
                  backgroundColor: cssVariables.backgroundLight,
                  borderRadius: '8px',
                  border: `1px solid ${cssVariables.borderLight}`
                }}>
                  <p style={{ 
                    margin: 0, 
                    color: cssVariables.textLight, 
                    fontSize: '14px',
                    width: window.innerWidth <= 768 ? '100%' : 'auto'
                  }}>
                    Menampilkan {assets.length} hasil untuk "{searchQuery}"
                  </p>
                  <button 
                    onClick={() => {
                      setSearchQuery("");
                      fetchAllAssets();
                    }} 
                    style={{
                      background: cssVariables.subtextLight,
                      color: 'white',
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '500',
                      transition: 'background-color 0.2s',
                      alignSelf: window.innerWidth <= 768 ? 'flex-end' : 'auto'
                    }}
                  >
                    Bersihkan
                  </button>
                </div>
              )}
            
            <div style={styles.grid}>
              {assets.map(asset => (
                <div key={asset.id} style={styles.card}>
                  <div style={styles.cardHeader}>
                    <h2 style={styles.cardTitle}>{asset.name}</h2>
                    <span 
                      style={{
                        ...styles.statusBadge,
                        backgroundColor: asset.write_at ? cssVariables.successLight : cssVariables.dangerLight,
                        color: asset.write_at ? cssVariables.successDark : cssVariables.danger
                      }}
                    >
                      {asset.write_at ? 'Terdaftar' : 'Belum Terdaftar'}
                    </span>
                  </div>
                  <div style={styles.cardActions}>
                    {canUpdate && (
                      <button 
                        onClick={() => openModal(asset)} 
                        style={styles.textButton}
                      >
                        Ubah
                      </button>
                    )}
                    {canDelete && (
                      <button 
                        onClick={() => confirmDelete(asset)}
                        style={styles.deleteButton}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#FEF2F2'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        Hapus
                      </button>
                    )}
                    {canStore && (
                      <button 
                        onClick={() => openNfcModal(asset)} 
                        style={styles.nfcButton}
                      >
                        NFC
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Modal Add/Edit Spot */}
      {modalOpen && (
        <div style={modalStyles.overlay}>
          <div style={modalStyles.modal}>
            <div style={modalStyles.modalHeader}>
              <h3 style={modalStyles.modalTitle}>
                {editingAsset ? 'Edit Spot Patroli' : 'Tambah Spot Patroli'}
              </h3>
              <button 
                onClick={() => setModalOpen(false)} 
                style={modalStyles.closeButton}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F3F4F6'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                ✕
              </button>
            </div>
            
            <div style={modalStyles.formGroup}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <label style={modalStyles.label}>Nama Spot</label>
                <span style={{
                  fontSize: '12px',
                  color: wordCount > 20 ? cssVariables.danger : cssVariables.subtextLight,
                  fontWeight: wordCount > 20 ? '600' : '400'
                }}>
                  {wordCount}/20 kata
                </span>
              </div>
              <input 
                name="name" 
                placeholder="Masukkan nama spot (maksimal 20 kata)" 
                value={formData.name} 
                onChange={handleChange} 
                style={{
                  ...modalStyles.input,
                  borderColor: wordCount > 20 ? cssVariables.danger : cssVariables.borderLight
                }}
                onFocus={(e) => e.target.style.borderColor = wordCount > 20 ? cssVariables.danger : cssVariables.primary}
                onBlur={(e) => e.target.style.borderColor = wordCount > 20 ? cssVariables.danger : cssVariables.borderLight}
              />
              {wordCount > 20 && (
                <small style={{ 
                  color: cssVariables.danger, 
                  fontSize: '12px', 
                  marginTop: '4px', 
                  display: 'block' 
                }}>
                  Nama spot melebihi batas 20 kata
                </small>
              )}
            </div>


            {editingAsset && (
              <div style={modalStyles.formGroup}>
                <label style={modalStyles.label}>Kode NFC</label>
                <div style={{
                  backgroundColor: '#F3F4F6',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'monospace',
                  color: cssVariables.textLight,
                  border: `1px solid ${cssVariables.borderLight}`
                }}>
                  {formData.code}
                </div>
                <small style={{ color: cssVariables.subtextLight, fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  Kode ini digunakan untuk identifikasi NFC
                </small>
              </div>
            )}

            <div style={modalStyles.buttonGroup}>
              <button 
                onClick={() => setModalOpen(false)} 
                style={modalStyles.cancelButton}
              >
                Batal
              </button>
              <button 
                onClick={handleSubmit} 
                style={modalStyles.submitButton}
                disabled={wordCount > 20}
              >
                {editingAsset ? 'Update Spot' : 'Simpan Spot'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NFC Modal */}
      {nfcModalOpen && nfcAsset && (
        <div style={nfcModalStyles.overlay}>
          <div style={nfcModalStyles.modal}>
            <div style={nfcModalStyles.modalHeader}>
              <h3 style={nfcModalStyles.modalTitle}>Tulis NFC Tag</h3>
              <button 
                onClick={() => setNfcModalOpen(false)} 
                style={nfcModalStyles.closeButton}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F3F4F6'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                ✕
              </button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <p style={{ color: cssVariables.textLight, marginBottom: '8px', fontSize: '16px' }}>
                Spot: <strong>{nfcAsset.name}</strong>
              </p>
              <p style={{ color: cssVariables.subtextLight, fontSize: '14px' }}>
                Kode: <code style={{
                  backgroundColor: '#F3F4F6',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontFamily: 'monospace',
                  color: cssVariables.textLight
                }}>{nfcAsset.code}</code>
              </p>
            </div>

            {nfcStatus && (
              <div style={{
                ...nfcModalStyles.nfcStatus,
                ...nfcModalStyles.nfcSuccess
              }}>
                {nfcStatus}
              </div>
            )}

            {nfcError && (
              <div style={{
                ...nfcModalStyles.nfcStatus,
                ...nfcModalStyles.nfcError
              }}>
                {nfcError}
              </div>
            )}

            {!nfcStatus && !nfcError && (
              <div style={{
                ...nfcModalStyles.nfcStatus,
                ...nfcModalStyles.nfcInfo
              }}>
                Siap menulis ke tag NFC
              </div>
            )}

            <div style={modalStyles.buttonGroup}>
              <button 
                onClick={() => setNfcModalOpen(false)} 
                style={modalStyles.cancelButton}
              >
                Batal
              </button>
              <button 
                onClick={writeTag} 
                style={modalStyles.submitButton}
                disabled={!!nfcStatus && nfcStatus.includes('berhasil')}
              >
                Tulis ke NFC
              </button>
            </div>

            {!supported && (
              <div style={{
                ...nfcModalStyles.nfcStatus,
                ...nfcModalStyles.nfcError,
                marginTop: '16px'
              }}>
                Browser tidak mendukung Web NFC. Gunakan Chrome di Android.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Custom Alert Modal */}
      {alert.show && (
        <AlertModal
          type={alert.type}
          title={alert.title}
          message={alert.message}
          onConfirm={alert.onConfirm}
          onClose={closeAlert}
          onCancel={closeAlert}
        />
      )}
    </div>
  )
}

if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style')
  styleSheet.innerHTML = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `
  document.head.appendChild(styleSheet)
}